import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IDefaultViewProps } from "./IDefaultViewProps";
import { Caption } from "../../atoms/views/Caption";
import { FileIcon } from "../../atoms/icons/FileIcon";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { useTranslation } from 'react-i18next';
import { IApplicationState, RootActionCreators } from "../../../store";
import { FetchState } from "../../../entities/FetchState";

// All the incoming values
interface IProps {
    clientId?: string | null;
    hideIcon?: boolean;
    hideLabel?: boolean;
}

// Outgoing functions
interface IDispatch {
}

// Actual Component
export type AllProps = IProps & IDispatch & Omit<IDefaultViewProps<any>, 'value'>;
export const Files = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const dispatch = useDispatch();
    
    // Get files from the store
    const filesState = useSelector((state: IApplicationState) => 
        state.client.clientFilesList.ClientFilesList
    );
    
    // Fetch files when client ID changes
    useEffect(() => {
        if (props.clientId) {
            dispatch(RootActionCreators.Client.clientFilesList.FetchData(props.clientId));
        }
    }, [props.clientId, dispatch]);
    
    // Handle row click to navigate to file detail (redirects to old database)
    const handleRowClick = (fileUuid: string) => {
        dispatch(RootActionCreators.Navigate.ToFileDetail(fileUuid));
    };
    
    const label = props.hideLabel ? undefined : (props.label || tClient('filesLabel'));
    const icon = props.hideIcon ? undefined : <FileIcon />;
    
    // Determine loading state
    const isLoading = props.loading || filesState.state === FetchState.Busy;
    
    // If no client ID provided
    if (!props.clientId) {
        return (<Caption loading={isLoading} caption={label} icon={icon}>
            {tClient('filesNoClientSelected')}
        </Caption>);
    }
    
    // If error state
    if (filesState.state === FetchState.Error) {
        return (<Caption loading={false} caption={label} icon={icon}>
            {filesState.errorMessage || tClient('filesError')}
        </Caption>);
    }
    
    // If no data or empty
    if (!filesState.data || filesState.data.length === 0) {
        if (isLoading) {
            return (<Caption loading={true} caption={label} icon={icon}>
                {tClient('filesLoading')}
            </Caption>);
        }
        return (<Caption loading={false} caption={label} icon={icon}>
            {tClient('filesEmpty')}
        </Caption>);
    }
    
    // Show data
    return (
        <Caption loading={isLoading} caption={label} icon={icon}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{tClient('fileMedewerker')}</TableCell>
                        <TableCell>{tClient('fileAangemeldOp')}</TableCell>
                        <TableCell>{tClient('fileStatus')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filesState.data.map((file) => (
                        <TableRow 
                            key={file.uuid}
                            onClick={() => handleRowClick(file.uuid)}
                            style={{ cursor: 'pointer' }}
                            hover
                        >
                            <TableCell>{file.mainEmployeeName || '-'}</TableCell>
                            <TableCell>
                                {file.date 
                                    ? new Date(file.date).toLocaleDateString('nl-BE', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit'
                                    })
                                    : '-'
                                }
                            </TableCell>
                            <TableCell>{file.status || '-'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Caption>
    );
}

